import type { ShoppingBagItem } from '@ori-events/basket-updated';
import { Typography, useTheme } from '@ori-ui/mui';
import { Placement } from '@ori/analytics-data';
import { CheckoutImage } from '@ori/checkout-ui';
import useTestId from '@ori/testid-generator';
import type { FC } from 'react';
import { useCallback } from 'react';

import { mainLogger } from '../../logger';
import type { HandleQuantityChangeOptions } from '../../types';
import { removeBasketItem, showErrorNotification, updateBasketItem } from '../../utils';
import { FormattedMessage } from '../FormattedMessage';
import { QuantityPicker } from '../QuantityPicker';
import { RemoveButton } from '../RemoveButton';
import { Shade } from '../Shade';
import {
  Discount,
  DiscountIcon,
  ImgLink,
  ItemShadeWrapper,
  Root,
  StyledItemDescription,
  StyledItemDetails,
  StyledItemInfoLink,
  StyledMiniBagWrapper,
  StyledName,
  StyledPrice,
  StyledPriceBlock,
  StyledPriceOld,
  StyledQuantityBlock,
} from './styles';

export const Item: FC<ShoppingBagItem> = ({
  itemCode,
  itemDescription,
  itemImageSrc,
  itemDetailPath,
  itemType,
  requestedQuantity,
  totalOriginalPrice,
  totalPrice,
  canBeDeleted,
  lineNumber,
  canBeModified,
  canBeReduced,
  maxQuantity,
  colorHexCode,
  colorImageSrc,
  itemColor,
}) => {
  const { getTestId } = useTestId();
  const theme = useTheme();

  const removeItemHandler = useCallback(async () => {
    try {
      // setState({
      //   isLoading: true,
      // });
      await removeBasketItem({
        itemCode,
        lineNumber,
        placement: Placement.Minibag,
        previousQuantity: requestedQuantity,
      });
    } catch (error: unknown) {
      // loading state & error already solved in shopping context
      // logError(error);
      // setState({
      //   isLoading: false,
      // });
    }
  }, [itemCode, lineNumber, requestedQuantity]);

  const handleQuantityChange = useCallback(
    async ({
      itemCode: code,
      lineNumber: line,
      quantity: currentQuantity,
      previousQuantity,
    }: HandleQuantityChangeOptions) => {
      if (currentQuantity === 0) {
        try {
          await removeItemHandler();
        } catch (error) {
          showErrorNotification('genericError');
          mainLogger.error(error as Error, { action: 'handleQuantityChange', quantity: 0 });
        }
      } else {
        try {
          await updateBasketItem({
            itemCode: code,
            lineNumber: line,
            placement: Placement.Minibag,
            previousQuantity,
            quantity: currentQuantity,
          });
        } catch (error) {
          showErrorNotification('genericError');
          mainLogger.error(error as Error, { action: 'handleQuantityChange', quantity: currentQuantity });
        }
      }
    },
    [removeItemHandler],
  );

  const partialId = `${lineNumber}-${itemCode}-${itemType}`;

  return (
    <Root
      id={`item-${partialId}-${totalPrice}`}
      data-testid={`${getTestId('item')}-${partialId}`}
    >
      {canBeDeleted ? (
        <RemoveButton
          data-testid="remove"
          onClick={removeItemHandler}
        />
      ) : null}
      <StyledMiniBagWrapper>
        <CheckoutImage
          component={ImgLink}
          href={itemDetailPath}
          src={itemImageSrc || 'noImage'}
          alt={itemDescription}
          background={theme.palette.grey[100]}
          width="80px"
          height="100px"
        />
        <StyledItemDescription>
          <StyledItemInfoLink href={itemDetailPath}>
            <StyledName>{itemDescription}</StyledName>
            {totalOriginalPrice ? (
              <Discount>
                <DiscountIcon />
              </Discount>
            ) : null}
          </StyledItemInfoLink>
          <ItemShadeWrapper>
            <Typography
              variant="caption"
              color="textSecondary"
            >
              {itemCode}
            </Typography>
            {(Boolean(colorHexCode) || Boolean(colorImageSrc)) && itemColor ? (
              <Shade
                data-testid="shade"
                colorHexCode={colorHexCode}
                colorImageSrc={colorImageSrc}
                colorName={itemColor}
              />
            ) : null}
          </ItemShadeWrapper>
          <StyledItemDetails>
            {!canBeReduced && !canBeModified ? (
              <StyledQuantityBlock>
                <FormattedMessage id="qty" />:{requestedQuantity}
              </StyledQuantityBlock>
            ) : (
              <QuantityPicker
                data-testid="quantity-picker"
                canBeDeleted={canBeDeleted}
                canBeModified={canBeModified}
                canBeReduced={canBeReduced}
                initialQuantity={requestedQuantity}
                itemCode={itemCode}
                lineNumber={lineNumber}
                maxQuantity={maxQuantity}
                onQuantityChange={handleQuantityChange}
              />
            )}
            <StyledPriceBlock>
              {totalOriginalPrice ? <StyledPriceOld>{totalOriginalPrice}</StyledPriceOld> : null}
              <StyledPrice>{totalPrice}</StyledPrice>
            </StyledPriceBlock>
          </StyledItemDetails>
        </StyledItemDescription>
      </StyledMiniBagWrapper>
    </Root>
  );
};
